/* React Higher Order Component which fetches activity meta tag data for activity (props.activityId)
    Child (WrappedComponent) will only render once meta tag data is successfully fetched.
    child will receive props: {
        channelId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        projectId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        activityId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        attachedMetaTags: PropTypes.object,
        attachedMetaType: PropTypes.object,
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const uuid = require('uuid');

const Globals = require('../../../../app/globals');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
const ChannelStore = require('../../stores/channel_store_reflux');
const ProjectStore = require('../../stores/ProjectStore');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    renderBeforeLodingTags: PropTypes.bool,
    forceRefresh: PropTypes.bool,
};

const defaultProps = {
    renderBeforeLodingTags: false,
    forceRefresh: false,
};

export default function withActivityMetaTags(WrappedComponent) {
    class WithActivityMetaTags extends React.Component {
        constructor(props) {
            super(props);

            const actStoreState = ActivityStore.getState();

            this.state = {
                activityToMetaTags: actStoreState.activityToMetaTags,
                activityToMetaType: actStoreState.activityToMetaType,
                getTagsActionId: null,
                getTypeActionId: null,
                fatalError: null
            };

            this.getActMetaTagsAndTypes = this.getActMetaTagsAndTypes.bind(this);
            this.isDataLoaded = this.isDataLoaded.bind(this);
            this.unsubscribes = [
                ActivityStore.listen(this.onActivityStoreChange.bind(this)),
                ChannelStore.listen(this.onChannelStoreChange.bind(this)),
                ProjectStore.listen(this.onProjectStoreChange.bind(this))
            ];
        }

        componentDidMount() {
            this.getActMetaTagsAndTypes(this.props.channelId, this.props.projectId, this.props.activityId, this.props.forceRefresh);
        }

        componentDidUpdate(prevProps) {
            if (
                prevProps.channelId !== this.props.channelId
                || prevProps.projectId !== this.props.projectId
                || prevProps.activityId !== this.props.activityId
            ) {
                this.getActMetaTagsAndTypes(this.props.channelId, this.props.projectId, this.props.activityId, this.props.forceRefresh);
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        isDataLoaded(activityId) {
            return (
                !!this.state.activityToMetaTags[activityId]
                && !!this.state.activityToMetaType[activityId]
            );
        }

        getActMetaTagsAndTypes(channelId, projectId, activityId, forceRefresh=false) {
            const channelStoreState = ChannelStore.getStateChannel(channelId);
            const projectStoreState = ProjectStore.getStateProject(projectId);
            if (
                channelStoreState.initialized
                && projectStoreState.initialized
                && FeatureUtil.hasActivityMetaTags(channelStoreState.channel.channel_permissions)
                && PermissionsUtil.canViewActMetaTagsForAct(channelStoreState.channel.user_permissions, projectStoreState.project.user_permissions)
            ) {
                if (
                    !forceRefresh
                    && this.isDataLoaded(activityId)
                ) { // if tags + categories & types are already populated do not refetch, unless forceRefresh is true
                    return;
                }

                const getTagsActionId = uuid.v4();
                const getTypeActionId = uuid.v4();
                this.setState({ getTagsActionId: getTagsActionId, getTypeActionId: getTypeActionId }, () => {
                    ActivityActions.getActMetaTagsForAct(getTagsActionId, { actId: activityId });
                    ActivityActions.getActMetaTypeForAct(getTypeActionId, { actId: activityId });
                });
            }
        }

        onActivityStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getTagsActionId) {
                additionalState = { ...additionalState, getTagsActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            if (newState.action.id === this.state.getTypeActionId) {
                additionalState = { ...additionalState, getTypeActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            const actStoreState = ActivityStore.getState();
            this.setState({
                activityToMetaTags: actStoreState.activityToMetaTags,
                activityToMetaType: actStoreState.activityToMetaType,
                ...additionalState
            });
        }

        onChannelStoreChange(newState) {
            this.getActMetaTagsAndTypes(this.props.channelId, this.props.projectId, this.props.activityId);
        }

        onProjectStoreChange(newState) {
            this.getActMetaTagsAndTypes(this.props.channelId, this.props.projectId, this.props.activityId);
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} />;
            }

            if (
                !this.isDataLoaded(this.props.activityId)
                && !this.props.renderBeforeLodingTags
            ) {
                return (
                    <div className="assignment-instructor">
                        <LoadingMainContent />
                    </div>
                );
            }

            return (
                <WrappedComponent
                    {...this.props}
                    attachedMetaTags={this.state.activityToMetaTags[this.props.activityId]}
                    attachedMetaType={this.state.activityToMetaType[this.props.activityId]}
                />
            );
        }
    }

    WithActivityMetaTags.propTypes = propTypes;
    WithActivityMetaTags.defaultProps = defaultProps;
    WithActivityMetaTags.displayName = `WithActivityMetaTags(${ReactUtil.getComponentDisplayName(WrappedComponent)})`;
    return WithActivityMetaTags;
}